/**
 * Player API Composable with Request Cancellation
 *
 * Provides a convenient interface for calling Player API endpoints with:
 * - Automatic request cancellation when new requests are made
 * - Automatic cleanup on component unmount
 * - Loading and error state management
 * - TypeScript type safety
 *
 * @example Basic usage
 * ```typescript
 * const { productList } = usePlayerApi();
 *
 * // Call endpoint
 * await productList.call({
 *   startDate: "2024-07-01",
 *   endDate: "2024-07-31",
 *   duration: 7,
 *   adults: 2,
 *   children: []
 * });
 *
 * // Access state
 * console.log(productList.loading.value);
 * console.log(productList.data.value);
 * console.log(productList.error.value);
 * ```
 *
 * @example Manual cancellation
 * ```typescript
 * const { productList } = usePlayerApi();
 *
 * await productList.call(params);
 * // User clicks cancel button
 * productList.cancel();
 * ```
 *
 * @example Automatic cancellation on rapid searches
 * ```typescript
 * const { productList } = usePlayerApi();
 *
 * watch(searchQuery, async (newQuery) => {
 *   // Previous request is automatically cancelled
 *   await productList.call(buildParams(newQuery));
 * });
 * ```
 */

import { ref, onUnmounted } from 'vue';
import type { GetProductListResult, GetProductOffersResult } from '@/types/schemas';
import {
  getProductList,
  getProductOffers,
  type GetProductListParams,
  type GetProductOffersParams,
} from '@/services/api/player';
import { PlayerApiError } from '@/types/errors';

/**
 * Composable for Player API calls with request cancellation
 *
 * Automatically cancels pending requests when:
 * - Component is unmounted
 * - New request is initiated
 *
 * @returns Object with productList and productOffers endpoint interfaces
 */
export function usePlayerApi() {
  // AbortControllers for request cancellation
  const productListController = ref<AbortController | null>(null);
  const productOffersController = ref<AbortController | null>(null);

  // GetProductList state
  const productListLoading = ref(false);
  const productListError = ref<string | null>(null);
  const productListData = ref<GetProductListResult | null>(null);

  // GetProductOffers state
  const productOffersLoading = ref(false);
  const productOffersError = ref<string | null>(null);
  const productOffersData = ref<GetProductOffersResult | null>(null);

  /**
   * Cancel pending GetProductList request
   */
  function cancelProductList(): void {
    if (productListController.value) {
      productListController.value.abort();
      productListController.value = null;
      productListLoading.value = false;
    }
  }

  /**
   * Cancel pending GetProductOffers request
   */
  function cancelProductOffers(): void {
    if (productOffersController.value) {
      productOffersController.value.abort();
      productOffersController.value = null;
      productOffersLoading.value = false;
    }
  }

  /**
   * Call GetProductList endpoint with cancellation support
   *
   * Automatically cancels any pending request before starting a new one.
   * Returns null if request was cancelled.
   *
   * @param params - Product list search parameters
   * @returns GetProductListResult or null if cancelled/failed
   *
   * @example
   * ```typescript
   * const result = await callGetProductList({
   *   startDate: "2024-07-01",
   *   endDate: "2024-07-31",
   *   duration: 7,
   *   adults: 2,
   *   children: []
   * });
   * ```
   */
  async function callGetProductList(
    params: GetProductListParams
  ): Promise<GetProductListResult | null> {
    // Cancel any pending request
    cancelProductList();

    // Create new AbortController
    const controller = new AbortController();
    productListController.value = controller;

    productListLoading.value = true;
    productListError.value = null;
    productListData.value = null;

    try {
      const { result } = await getProductList(params, {
        signal: controller.signal,
      });

      // Check if request was cancelled
      if (controller.signal.aborted) {
        return null;
      }

      if (!result) {
        throw new Error('No result returned from API');
      }

      productListData.value = result;

      // Check for API-level errors (Success=false)
      if (!result.Success) {
        const errorMessages =
          result.ProcessMessages?.ProcessMessage?.filter(
            (msg) => msg.MessageType === 'Error' || msg.MessageType === 'ConfigurationError'
          )
            .map((msg) => msg.Message)
            .filter(Boolean) || [];

        productListError.value = errorMessages.join(', ') || 'API returned Success=false';
      }

      return result;
    } catch (err) {
      // Ignore cancellation errors
      if (err instanceof PlayerApiError && err.code === 'REQUEST_CANCELLED') {
        return null;
      }

      if (err instanceof PlayerApiError) {
        productListError.value = `${err.code}: ${err.message}`;
      } else if (err instanceof Error) {
        productListError.value = err.message;
      } else {
        productListError.value = 'Unknown error occurred';
      }
      throw err;
    } finally {
      // Only clear loading if this controller is still active
      if (productListController.value === controller) {
        productListLoading.value = false;
        productListController.value = null;
      }
    }
  }

  /**
   * Call GetProductOffers endpoint with cancellation support
   *
   * Automatically cancels any pending request before starting a new one.
   * Returns null if request was cancelled.
   *
   * @param params - Product offers parameters
   * @returns GetProductOffersResult or null if cancelled/failed
   *
   * @example
   * ```typescript
   * const result = await callGetProductOffers({
   *   productCode: "ABC123",
   *   startDate: "2024-07-15",
   *   endDate: "2024-07-22",
   *   duration: 7,
   *   adults: 2,
   *   children: []
   * });
   * ```
   */
  async function callGetProductOffers(
    params: GetProductOffersParams
  ): Promise<GetProductOffersResult | null> {
    // Cancel any pending request
    cancelProductOffers();

    // Create new AbortController
    const controller = new AbortController();
    productOffersController.value = controller;

    productOffersLoading.value = true;
    productOffersError.value = null;
    productOffersData.value = null;

    try {
      const { result } = await getProductOffers(params, {
        signal: controller.signal,
      });

      // Check if request was cancelled
      if (controller.signal.aborted) {
        return null;
      }

      if (!result) {
        throw new Error('No result returned from API');
      }

      productOffersData.value = result;

      // Check for API-level errors (Success=false)
      if (!result.Success) {
        const errorMessages =
          result.ProcessMessages?.ProcessMessage?.filter(
            (msg) => msg.MessageType === 'Error' || msg.MessageType === 'ConfigurationError'
          )
            .map((msg) => msg.Message)
            .filter(Boolean) || [];

        productOffersError.value = errorMessages.join(', ') || 'API returned Success=false';
      }

      return result;
    } catch (err) {
      // Ignore cancellation errors
      if (err instanceof PlayerApiError && err.code === 'REQUEST_CANCELLED') {
        return null;
      }

      if (err instanceof PlayerApiError) {
        productOffersError.value = `${err.code}: ${err.message}`;
      } else if (err instanceof Error) {
        productOffersError.value = err.message;
      } else {
        productOffersError.value = 'Unknown error occurred';
      }
      throw err;
    } finally {
      // Only clear loading if this controller is still active
      if (productOffersController.value === controller) {
        productOffersLoading.value = false;
        productOffersController.value = null;
      }
    }
  }

  // Cancel all pending requests on unmount
  onUnmounted(() => {
    cancelProductList();
    cancelProductOffers();
  });

  return {
    // GetProductList
    productList: {
      loading: productListLoading,
      error: productListError,
      data: productListData,
      call: callGetProductList,
      cancel: cancelProductList,
    },

    // GetProductOffers
    productOffers: {
      loading: productOffersLoading,
      error: productOffersError,
      data: productOffersData,
      call: callGetProductOffers,
      cancel: cancelProductOffers,
    },
  };
}
